import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {trigger, transition, style, animate} from "@angular/animations";
import {Message} from "./message/message.model";
import {AuthenticationService} from "../../../core/services/services/authentication.service";
import {TranslatableComponent} from "../../../core/models/TranslatableComponent";
import {TranslateService} from "@ngx-translate/core";
import {MessageHandler} from "./messageHandler";
import {ResourceRequestListService} from "../../resource-request-list/services/resource-request-list.service";
import {ChatBotService} from "../../../core/services/services/chat-bot.service";
import {MatTooltip} from "@angular/material/tooltip";
import {MatIcon} from "@angular/material/icon";
import {ChatMessageComponent} from "./message/message.component";
import {ReactiveFormsModule, FormsModule} from "@angular/forms";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatMenuTrigger, MatMenu, MatMenuItem} from "@angular/material/menu";
import {MatCheckbox} from "@angular/material/checkbox";

@Component({
	selector: "app-chat",
	templateUrl: "./chat.component.html",
	styleUrls: ["./chat.component.scss"],
	animations: [
		trigger("llmPanel", [
			transition(":enter", [
				style({opacity: 0, transform: "translateY(12px) scale(0.995)"}),
				animate("240ms cubic-bezier(0.2, 0.9, 0.2, 1)", style({opacity: 1, transform: "translateY(0) scale(1)"})),
			]),
			transition(":leave", [
				animate(
					"180ms cubic-bezier(0.4, 0.0, 0.2, 1)",
					style({opacity: 0, transform: "translateY(10px) scale(0.995)"}),
				),
			]),
		]),
	],
	imports: [
		MatTooltip,
		MatIcon,
		ChatMessageComponent,
		ReactiveFormsModule,
		FormsModule,
		MatButton,
		MatMenuTrigger,
		MatMenu,
		MatMenuItem,
		MatCheckbox,
		MatIconButton,
	],
})
export class ChatComponent extends TranslatableComponent implements OnInit, OnDestroy {
	messages: Message[] = [];
	llmResponses: string[] = []; // Optional: store all LLM responses for analysis
	inputText: string = "";
	placeholderOpacity: number = 0.55;
	ignoreCapa: boolean;
	crossBusinessUnitSearch: boolean = true;
	minimumAvailability: number = 25;
	showCheatSheet: boolean = false;
	showLlmResponse: boolean = false;
	activeLlmResponse: string | null = null;

	// Animation properties
	flyingText: string = "";
	showFlyingText: boolean = false;
	flyingTextPosition = {x: 0, y: 0};

	@ViewChild("inputTextArea") inputTextArea!: ElementRef<HTMLTextAreaElement>;
	@ViewChild("chatMessagesWindow") chatMessagesWindow!: ElementRef<HTMLDivElement>;

	private messageHandler: MessageHandler;
	private hasActiveErrorMessage: boolean = false;

	constructor(
		private chatbotService: ChatBotService,
		private authService: AuthenticationService,
		private resourceRequestService: ResourceRequestListService,
		private _translate: TranslateService,
	) {
		super(_translate, "ChatBotComponent");
	}

	async ngOnInit(): Promise<void> {
		let injectedStaffingId = history?.state.data;
		await this.sendMessage(this._lang("starting-message"), "Mephisto");
		this.messageHandler = new MessageHandler(this.chatbotService);
		this.messageHandler.openConnection();

		if (injectedStaffingId !== undefined) {
			await this.sendMessage(`Staffing ${injectedStaffingId}`, "Du");
		}

		this.messageHandler.messageSubject.subscribe(async (message) => {
			switch (message.type) {
				case "MESSAGE": {
					this.removeErrorMessages();
					const llmResponse = message.data && "llmResponse" in message.data ? message.data.llmResponse : undefined;
					if (llmResponse) {
						this.llmResponses.push(llmResponse);
						this.activeLlmResponse = llmResponse;
					}
					await this.sendMessage(message.output, "Mephisto", false, llmResponse);
					break;
				}
				case "ERROR":
					await this.sendMessage(message.output, "Mephisto", true);
					this.hasActiveErrorMessage = true;
					break;
				case "INFO":
					this.handleUpdateMessages(message.output);
					break;
				case "NODATA":
					this.removeErrorMessages();
					await this.sendMessage(message.output, "Mephisto");
					break;
			}
		});
	}

	async sendMessage(newMessage: string, sender: string, isError: boolean = false, llmResponse?: string): Promise<void> {
		if (newMessage?.trim()) {
			const message: Message = {
				text: newMessage.trim(),
				sender: sender,
				loadingMessage: false,
				isError: isError,
				llmResponse: llmResponse, // Attach LLM response if present
			};
			if (sender === "Mephisto") this.messages.pop();
			this.messages.push(message);

			if (sender === "Du") {
				this.removeErrorMessages();
				this.startConversation();
				const val = await this.authService.getApplicationUser();

				const businessUnit = val.employee.businessUnit;

				await this.chatbotService.sendMessage(
					message.text,
					businessUnit,
					this.ignoreCapa,
					this.minimumAvailability,
					this.crossBusinessUnitSearch,
				);

				setTimeout(() => {
					this.adjustTextAreaHeight();
				}, 0);
			} else {
				setTimeout(() => {
					this.resetTextArea();
				}, 0);
			}
			setTimeout(() => {
				this.scrollToBottom();
			}, 0);
		}
	}

	changeCrossBusinessUnitFlag() {
		this.crossBusinessUnitSearch = !this.crossBusinessUnitSearch;
	}

	private startConversation() {
		this.placeholderOpacity = 1;
		this.inputTextArea.nativeElement.readOnly = true;
		this.inputText = "";
		this.addLoadingMessage();
	}

	async checkEnter(event: KeyboardEvent) {
		if (event.key === "Enter") {
			if (event.shiftKey) {
				this.inputText += "\n";
				setTimeout(() => {
					this.adjustTextAreaHeight();
				}, 0);
			} else {
				await this.sendMessage(this.inputText, "Du");
				event.preventDefault();
			}
		}
	}

	adjustTextAreaHeight() {
		const textArea = this.inputTextArea.nativeElement;
		const maxHeight: number = 25;
		if (textArea.scrollHeight < maxHeight) {
			textArea.style.height = "3.5rem";
			textArea.style.height = textArea.scrollHeight + "rem";
		}
	}

	scrollToBottom() {
		const chatMessagesWindow = this.chatMessagesWindow.nativeElement;
		chatMessagesWindow.scrollTo({
			top: chatMessagesWindow.scrollHeight,
			behavior: "smooth",
		});
	}

	handleUpdateMessages(text: string) {
		if (text?.trim()) {
			this.inputTextArea.nativeElement.placeholder = text;
		}
	}

	resetTextArea() {
		const inputTextArea = this.inputTextArea.nativeElement;
		inputTextArea.readOnly = false;
		inputTextArea.placeholder = "Nachricht eingeben...";
		this.placeholderOpacity = 0.55;
	}

	addLoadingMessage() {
		const message: Message = {
			text: "\t\t\t",
			sender: "Mephisto",
			loadingMessage: true,
			isError: false,
		};
		this.messages.push(message);
	}

	changeCapaFlag() {
		this.ignoreCapa = !this.ignoreCapa;
	}

	onCopyPrompt(text: string, event: MouseEvent): void {
		const clickX = event.clientX;
		const clickY = event.clientY;

		const inputRect = this.inputTextArea.nativeElement.getBoundingClientRect();
		const targetX = inputRect.left + inputRect.width / 2;
		const targetY = inputRect.top + inputRect.height / 2;

		this.flyingText = text.length > 50 ? text.substring(0, 50) + "..." : text;
		this.flyingTextPosition = {x: clickX, y: clickY};
		this.showFlyingText = true;

		setTimeout(() => {
			this.flyingTextPosition = {x: targetX, y: targetY};
		}, 10);

		// After animation completes, set the text and hide flying text
		setTimeout(() => {
			this.inputText = text;
			this.showFlyingText = false;

			setTimeout(() => {
				const textArea = this.inputTextArea.nativeElement;
				textArea.focus();
				this.adjustTextAreaHeight();
				textArea.setSelectionRange(text.length, text.length);
			}, 0);
		}, 600);
	}

	openLlmResponsePanel(): void {
		this.showLlmResponse = !this.showLlmResponse;
	}

	ngOnDestroy() {
		if (this.messageHandler) {
			this.messageHandler.ngOnDestroy();
		}
	}

	private removeErrorMessages() {
		if (this.hasActiveErrorMessage) {
			this.messages = this.messages.filter((msg) => !msg.isError);
			this.hasActiveErrorMessage = false;
		}
	}
}
